import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Flag, ShieldCheck, EyeOff, ExternalLink } from "lucide-react";
import { PROFESSIONAL_LABELS } from "@/components/SharedRitualsFeed";

const REASON_LABELS = {
  feels_like_a_pitch: "Feels like a pitch",
  misleading_claims: "Misleading claims",
  off_topic: "Off-topic",
  other: "Something else",
};

/**
 * RitualReportsPanel — admin-only queue of member reports on shared rituals.
 *
 * Every report is either kept (ritual stays public) or hidden (ritual is
 * pulled from the browse feed). Reports are private to the admin — the
 * flagged professional never sees who reported them. The panel is mounted
 * inside the existing /moderation page so we don't spin up a new route.
 */
export default function RitualReportsPanel() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async (t = tab) => {
    setLoading(true);
    try {
      const { data } = await endpoints.adminListRitualReports(t);
      setItems(data || []);
    } catch (e) {
      // Non-admins hit 403 and we simply hide the panel.
      setItems([]);
    }
    setLoading(false);
  };

  useEffect(() => { load(tab); /* eslint-disable-next-line */ }, [tab]);

  const resolve = async (id, action) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await endpoints.adminResolveRitualReport(id, action);
      toast.success(action === "hide" ? "Ritual hidden from the feed." : "Kept — nothing more to do.");
      await load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve.");
    }
    setBusy((b) => ({ ...b, [id]: false }));
  };

  return (
    <section className="pt-4" data-testid="ritual-reports-panel">
      <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
        Shared Rituals
      </div>
      <h2 className="font-display text-3xl mt-2 tracking-tight italic">
        <Flag size={16} className="inline-block mb-1 mr-1.5 text-[hsl(var(--terracotta))]" />
        Reported by members
      </h2>
      <p className="mt-2 max-w-2xl text-sm text-[hsl(var(--muted-foreground))]">
        Members flag rituals that read like sales pitches or contain misleading claims. Kept means the ritual stays;
        hidden pulls it from the browse feed immediately.
      </p>

      <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="ritual-reports-tabs">
        {["pending", "hidden", "kept", "all"].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            aria-pressed={tab === k}
            data-testid={`ritual-reports-tab-${k}`}
            className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.18em] border hairline tab-link ${
              tab === k
                ? "bg-[hsl(var(--fg))] text-[hsl(var(--paper))] border-transparent"
                : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
            }`}
          >
            {k}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-6 text-sm text-[hsl(var(--muted-foreground))]" data-testid="ritual-reports-loading">
          Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          className="mt-6 p-8 text-center rounded-md border hairline bg-[hsl(var(--paper))]"
          data-testid="ritual-reports-empty"
        >
          <div className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            Nothing here
          </div>
          <p className="mt-2 font-display text-xl italic">
            {tab === "pending" ? "The queue is clear — no rituals need review right now." : "No reports in this state."}
          </p>
        </div>
      ) : (
        <ul className="mt-6 space-y-4">
          {items.map((rep) => {
            const r = rep.ritual || {};
            const typeLabel = PROFESSIONAL_LABELS[r.professional_type] || r.professional_type || "Wellness Professional";
            return (
              <li
                key={rep.id}
                className="p-5 border hairline rounded-md bg-[hsl(var(--paper))]"
                data-testid={`ritual-report-item-${rep.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-[hsl(var(--terracotta))]/12 text-[hsl(var(--terracotta))] border border-[hsl(var(--terracotta))]/25">
                        {typeLabel}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                        {REASON_LABELS[rep.reason] || rep.reason}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
                        · reported {new Date(rep.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-xl leading-tight">
                      {r.title || "(ritual removed)"}
                    </div>
                    {r.spa_name && (
                      <div className="text-sm text-[hsl(var(--muted-foreground))]">by {r.spa_name}{r.city ? ` · ${r.city}` : ""}</div>
                    )}
                    {rep.note && (
                      <p className="mt-3 text-sm italic leading-relaxed">"{rep.note}"</p>
                    )}
                    <div className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))]">
                      Flagged by {rep.reporter_name || "a member"}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {r.code && (
                      <Link
                        to={`/r/${r.code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`ritual-report-open-${rep.id}`}
                        className="text-xs inline-flex items-center gap-1 underline underline-offset-4 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))]"
                      >
                        Open <ExternalLink size={11} />
                      </Link>
                    )}
                    {rep.status === "pending" ? (
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!!busy[rep.id]}
                          onClick={() => resolve(rep.id, "keep")}
                          data-testid={`ritual-report-keep-${rep.id}`}
                          className="rounded-full"
                        >
                          <ShieldCheck size={13} className="mr-1.5" /> Keep
                        </Button>
                        <Button
                          size="sm"
                          disabled={!!busy[rep.id]}
                          onClick={() => resolve(rep.id, "hide")}
                          data-testid={`ritual-report-hide-${rep.id}`}
                          className="rounded-full bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 text-[hsl(var(--paper))]"
                        >
                          <EyeOff size={13} className="mr-1.5" /> Hide
                        </Button>
                      </div>
                    ) : (
                      <span className={`text-[11px] uppercase tracking-[0.18em] px-2 py-1 rounded-full border hairline ${
                        rep.status === "hidden"
                          ? "text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30"
                          : "text-[hsl(var(--moss))] border-[hsl(var(--moss))]/40"
                      }`}>
                        {rep.status}
                      </span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
